import { CheckInsRequestParam } from 'src/app/utils/interfaces/new/check-ins-request-param';
import { InsIdentityInfos } from 'src/app/utils/interfaces/new/ins-identity-infos.interface';
import { LectureIns } from 'src/app/utils/interfaces/new/lecture-ins.interface';

/**
 * Construction des détails du journal fonctionnel (DomainEnum.Insi),
 * partagée par les stratégies websocket et REST. L'émission (_addLog) reste propre à chaque stratégie.
 */

export const INS_FROM_IDENTITY_LOG_SUBDOMAIN = 'Récupération de l\'INS depuis les informations d\'identité';
export const INS_FROM_VITALE_LOG_SUBDOMAIN = 'Récupération de l\'INS depuis la carte Vitale';
export const CHECK_INS_LOG_SUBDOMAIN = 'Vérification INS identité';

export const INS_RETOUR_LOG_HEADER = 'Retour :\n';
export const INS_UNIQUE_IDENTITY_LOG_HEADER = INS_RETOUR_LOG_HEADER + '00 - une identité unique a été trouvée';
export const INS_NO_IDENTITY_LOG_DETAILS = INS_RETOUR_LOG_HEADER + '01 - Aucune identité trouvée';
export const INS_MULTIPLE_IDENTITIES_LOG_DETAILS = INS_RETOUR_LOG_HEADER + '02 - Plusieurs identités trouvées';

export function getSexName(patientIns: LectureIns): string {
  switch (patientIns?.Identity?.i_sex) {
    case 2:
      return 'Masculin';
    case 3:
      return 'Féminin';
    default:
      return 'Inconnu';
  }
}

export function getInsLabel(oid: string): string {
  switch (oid) {
    case '1.2.250.1.213.1.4.8':
      return 'INS-NIR';
    case '1.2.250.1.213.1.4.9':
      return 'INS-NIA';
    case '1.2.250.1.213.1.4.10':
      return 'INS-NIR de test';
    case '1.2.250.1.213.1.4.11':
      return 'INS-NIR de démonstration';
    default:
      return 'INS';
  }
}

/**
 * Traits d'identité et INS (courant et historisés) retournés par l'INSi.
 *
 * @param header Ligne ajoutée en tête des détails (ex : INS_UNIQUE_IDENTITY_LOG_HEADER)
 */
export function buildIdentityLogDetails(lectureIns: LectureIns, header?: string): string {
  const identity = lectureIns?.Identity;
  const oid = identity?.Ins?.s_oid ? identity.Ins.s_oid : 'Inconnu';
  const lines: Array<string> = [];
  if (header) {
    lines.push(header);
  }
  lines.push(`Nom de naissance : ${identity?.s_birthName ? identity.s_birthName : 'Inconnu'}`);
  lines.push(`Prénom de naissance : ${identity?.s_birthGiven ? identity.s_birthGiven : 'Inconnu'}`);
  lines.push(`Date de naissance : ${identity?.s_birthDate ? identity.s_birthDate : 'Inconnue'}`);
  lines.push(`Sexe : ${getSexName(lectureIns)}`);
  lines.push(`Lieu de naissance : ${identity?.s_birthPlace ? identity.s_birthPlace : 'Inconnu'}`);
  const insValue = identity?.Ins?.s_value ? identity.Ins.s_value : 'Inconnue';
  const insKey = identity?.Ins?.s_key ? identity.Ins.s_key : 'Inconnue';
  lines.push(`${getInsLabel(oid)} : ${insValue} ${insKey} (${oid})`);

  let details = lines.join('\n\n');
  if (identity?.History) {
    let historyStr = '\nHistorique INS : ';
    for (const h of identity.History) {
      historyStr += `\n- ${getInsLabel(h.s_oid)} : ${h.s_value} ${h.s_key} (${h.s_oid}) / Du ${h.s_startDate} au ${h.s_endDate}`;
    }
    details += `\n${historyStr}`;
  }
  return details;
}

/**
 * Détails d'une erreur INSi : response.error du connecteur websocket ou corps d'erreur du proxy REST.
 *
 * @param withCodes Ajoute le code et le type de l'erreur (utilisé pour la vérification d'identité)
 */
export function buildInsiErrorLogDetails(error: any, withCodes = false): string {
  const detailsArr: Array<string> = [];
  if (withCodes && error?.i_apiErrorCode) {
    detailsArr.push(`Code erreur : ${error.i_apiErrorCode}`);
  }
  if (withCodes && error?.i_apiErrorType) {
    detailsArr.push(`Type erreur : ${error.i_apiErrorType}`);
  }
  if (error?.s_apiErrorDescription) {
    detailsArr.push(`Description : ${error.s_apiErrorDescription}`);
  }
  if (error?.s_apiErrorTlsiErrorDetailCode) {
    detailsArr.push(`Détail code : ${error.s_apiErrorTlsiErrorDetailCode}`);
  }
  if (error?.s_apiErrorTlsiErrorDetailError) {
    detailsArr.push(`Détail : ${error.s_apiErrorTlsiErrorDetailError}`);
  }
  if (error?.s_apiErrorExtendedInformations) {
    detailsArr.push(`Informations supplémentaires : ${error.s_apiErrorExtendedInformations}`);
  }
  return detailsArr.join('\n');
}

/**
 * Traits d'identité soumis à la recherche d'INS (WS_INS2), placés en tête des détails.
 * Les noms et prénoms sont attendus déjà normalisés (majuscules sans accents).
 */
export function buildIdentitySearchLogDetails(identity: InsIdentityInfos): string {
  return 'Recherche :\n' +
    'Nom de naissance : ' + identity.s_birthName + '\n' +
    '1er Prénom de naissance : ' + identity.s_given + '\n' +
    'Date de naissance : ' + identity.s_birthDate + '\n' +
    'Lieu de naissance : ' + (identity.s_birthPlace || 'Inconnu') + '\n\n';
}

/**
 * Requête INSi et ses paramètres, placés en fin des détails.
 *
 * @param requestName Commande du connecteur websocket ou route du proxy REST
 * @param params Paramètres de la requête, hors LpsInfos (une ligne par paramètre)
 */
export function buildInsiRequestLogDetails(requestName: string, lpsInfos: { s_name?: string, s_version?: string }, params: Array<string> = []): string {
  return `\n\nRequête: ${requestName}\nParamètres:\nLpsInfos : ${lpsInfos?.s_name}:${lpsInfos?.s_version}` +
    params.map(p => `\n${p}`).join('');
}

/**
 * Paramètres d'une recherche d'INS par traits, pour buildInsiRequestLogDetails.
 * Les noms et prénoms sont attendus déjà normalisés (majuscules sans accents).
 */
export function buildIdentityRequestLogParams(identity: InsIdentityInfos): Array<string> {
  return [
    `Nom de naissance :${identity.s_birthName}`,
    `1er Prénom de naissance :${identity.s_given}`,
    `Date de naissance :${identity.s_birthDate}`,
    `Sexe :${identity.i_sex}`,
    `Lieu de naissance :${identity.s_birthPlace || 'Non renseigné'}`
  ];
}

/**
 * Traits d'identité soumis à la vérification d'identité (WS_INS3).
 *
 * @param normalizeName Mise en forme des noms et prénoms (majuscules sans accents)
 */
export function buildCheckInsRequestLogDetails(param: CheckInsRequestParam, normalizeName: (str: string) => string): string {
  return 'Recherche : \n' +
    'Nom de naissance :' + normalizeName(param.s_birthName) + '\n' +
    '1er Prénom de naissance :' + normalizeName(param.s_given) + '\n' +
    'Date de naissance :' + param.s_birthDate + '\n' +
    'Lieu de naissance :' + param.s_birthPlace + '\n' +
    'INS :' + param.s_ins + '-' + param.s_key + '-' + param.s_oid + '\n\n';
}
